import './FocusIndicator.scss';

import * as React from 'react';

import cx from 'classnames';
import useWhatInput from 'react-use-what-input';

export interface Props {
  /** A utility class applying a text colour, from which the outline inherits */
  colorClass?: string;
  /** Outline is offset by default, use this to override */
  notOffset?: boolean;
  /** Includes an inset white outline in addition to the coloured outline */
  includesWhiteInset?: boolean;
  /** Outline is rendered "within" the container — used in conjunction with `notOffset` */
  isInset?: boolean;
  /** Should the indicators display in all cases (only during keyboard interaction by default) */
  alwaysVisible?: boolean;
}

/**
 * The focus indicator shows a coloured outline around the focused element.
 * This works by being slightly larger than the container.
 */
export const FocusIndicator = ({
  colorClass,
  notOffset,
  includesWhiteInset,
  isInset,
  alwaysVisible,
}: Props) => {
  const isUsingKeyboard = useWhatInput('intent') === 'keyboard';
  const shouldHideIndicator = !alwaysVisible && !isUsingKeyboard;
  const indicatorClass = React.useMemo(
    () =>
      cx(
        'absolute pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity duration-100 ease-linear',
        'focus-indicator',
        {
          'shadow-outline': !includesWhiteInset && !isInset,
          '-inset-2': !notOffset,
          'inset-0 not-offset': notOffset,
          'text-cyan-600': !colorClass,
          'with-white-inset': includesWhiteInset,
          'is-inset': isInset,
          'is-hidden': shouldHideIndicator,
          [colorClass as string]: !!colorClass,
        }
      ),
    [colorClass, notOffset, includesWhiteInset, isInset, shouldHideIndicator]
  );

  return <div className={indicatorClass} />;
};
